/**
 * Heartbeat-derived connectivity state.
 *
 * navigator.onLine === false is trustworthy, but onLine === true only means
 * "some interface is up" — with Wi-Fi off, virtual adapters (WSL, VPN,
 * Hyper-V) keep it true while nothing routes. Since the audit-change poll
 * fires every second whenever someone is logged in, its outcomes are a free
 * probe: consecutive network-level failures mean we are effectively offline,
 * the first success means we are back.
 */

/** Single blips must not flap the offline notice; ~3s of the 1s poll. */
const OFFLINE_AFTER_CONSECUTIVE_FAILURES = 3;

let consecutiveFailures = 0;
let derivedOffline = false;

type ConnectivityListener = (offline: boolean) => void;
const listeners = new Set<ConnectivityListener>();

function emit(offline: boolean) {
  listeners.forEach((listener) => listener(offline));
}

export function reportNetworkFailure(): void {
  consecutiveFailures += 1;
  if (
    !derivedOffline &&
    consecutiveFailures >= OFFLINE_AFTER_CONSECUTIVE_FAILURES
  ) {
    derivedOffline = true;
    emit(true);
  }
}

export function reportNetworkSuccess(): void {
  consecutiveFailures = 0;
  if (derivedOffline) {
    derivedOffline = false;
    emit(false);
  }
}

/**
 * The one connectivity answer the app consults: hard offline (browser says
 * so) OR derived offline (the heartbeat says nothing is getting through).
 */
export function isConnectivityOffline(): boolean {
  const browserOffline =
    typeof navigator !== "undefined" && navigator.onLine === false;
  return browserOffline || derivedOffline;
}

/**
 * Trouble is brewing but unconfirmed: failures started, the threshold hasn't
 * been reached. Callers stay quiet in this window — a slow network must not
 * flash a popup that the next heartbeat may retract.
 */
export function isNetworkTroubleSuspected(): boolean {
  return !derivedOffline && consecutiveFailures > 0;
}

/** Fires on derived-state transitions only. Returns an unsubscribe. */
export function onConnectivityChange(
  listener: ConnectivityListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
